/**
 * audio-scheduler.ts — main-thread Web-Audio scheduling.
 *
 * Owns the AudioContext + AudioBufferSourceNode ring + the media-clock
 * anchor. Receives PCM channel data (Float32Array per channel) plus the
 * media-PTS in microseconds, builds an AudioBuffer, schedules it on the
 * AudioContext output graph, and advances the clock anchor.
 *
 * Why this lives on the main thread (not in the worker): Web Audio is
 * spec'd as main-thread-only — AudioContext + AudioBufferSourceNode +
 * .start() / .connect() / suspend()/resume() do not exist in
 * DedicatedWorkerGlobalScope. The worker decodes audio packets via
 * WebCodecs.AudioDecoder, copies the PCM out as Float32Arrays, and
 * transfers those to main where this class takes over.
 *
 * The currentTimeSec getter exposes the master media-clock that the
 * video presentation in the worker reads via 10 ms postMessage updates.
 */

// ── Configuration ────────────────────────────────────────────────────

export interface AudioSchedulerOptions {
  /** Optional pre-existing AudioContext to share with the caller. */
  audioCtx?: AudioContext;
  /** Called on internal errors (rare — node.start() errors etc.). */
  onError?:  (err: Error) => void;
}

/**
 * AudioScheduler — main-thread half of the audio pipeline. Receives
 * PCM frames (channels: Float32Array[]) + media-PTS in microseconds,
 * schedules them on AudioContext, and tracks the playback clock.
 *
 * Lifecycle:
 *   const sched = new AudioScheduler({});
 *   sched.scheduleBuffer(ptsUs, [chL, chR], 48000);   // many calls
 *   await sched.start();                              // resume ctx
 *   sched.pause();
 *   sched.swap();                                     // on seek
 *   sched.close();
 */
export class AudioScheduler {
  private ctx:     AudioContext;
  private playing = false;
  private closed  = false;

  /** Queue of buffers received before start() — scheduled in start(). */
  private pendingBuffers: { buf: AudioBuffer; ptsUs: number }[] = [];
  /** Currently-scheduled source-nodes for cleanup at swap()/close(). */
  private liveNodes: AudioBufferSourceNode[] = [];

  // ── Media-clock anchor ─────────────────────────────────────────────
  // AudioContext.currentTime is wall-time-since-context-create. The
  // video pipeline needs media-PTS-seconds. Anchor on the first scheduled
  // buffer:
  //   anchorPtsSec = first buffer's media-PTS in seconds
  //   anchorCtxSec = AudioContext.currentTime when scheduled
  // Then currentTimeSec = ctx.currentTime - anchorCtxSec + anchorPtsSec.
  // Reset on swap() so a post-seek anchor takes hold at the new PTS.
  private anchorPtsSec: number = 0;
  private anchorCtxSec: number = -1;
  private nextScheduleCtxSec: number = 0;

  /// Audio-swap sync-fix S4 (2026-05-10): override-target for the next
  /// anchor. When setNextAnchor(T) is called and a buffer arrives whose
  /// PTS-range covers T, we anchor at T (= matches master-clock) and
  /// play the buffer with offset = T - buffer.startPts. Buffers entirely
  /// stale (PTS-range past T) are dropped without anchoring.
  private nextAnchorTargetSec: number | null = null;
  // Wall-clock when setNextAnchor was called. Used to project the target
  // forward by the elapsed swap-wait time, so the master-clock continues
  // advancing through the catch-up wait instead of snapping back to the
  // click-instant target on first-buffer-arrival.
  private nextAnchorIssuedAtMs: number = 0;
  // 'swap' (= audio-rendition-switch) projects target forward by elapsed
  // wallclock so master-clock stays monotonic across the catch-up wait.
  // 'seek' (= seek-to-position) doesn't project — master is pinned at
  // pendingSeekTimeSec during warmup, and the seek-target gets up to
  // 15s of encoder warmup. Forward-project would drop the first new
  // buffer if warmup > buffer-duration.
  private nextAnchorMode: 'swap' | 'seek' = 'swap';

  constructor(private readonly opts: AudioSchedulerOptions = {}) {
    this.ctx = opts.audioCtx ?? new AudioContext({ latencyHint: 'playback' });
  }

  /**
   * Schedule a PCM buffer for playback. `channels` is one Float32Array
   * per channel (planar f32 — same convention WebCodecs.AudioData uses).
   * `ptsUs` is media-PTS in microseconds.
   */
  scheduleBuffer(ptsUs: number, channels: Float32Array[], sampleRate: number): void {
    if (this.closed) return;
    if (channels.length === 0) return;

    const numFrames = channels[0].length;
    const audioBuf = this.ctx.createBuffer(channels.length, numFrames, sampleRate);
    for (let ch = 0; ch < channels.length; ++ch) {
      audioBuf.getChannelData(ch).set(channels[ch]);
    }

    if (this.playing) {
      this.scheduleNode(audioBuf, ptsUs);
    } else {
      this.pendingBuffers.push({ buf: audioBuf, ptsUs });
    }
  }

  /** Resume AudioContext (must come from a user-gesture). */
  async start(): Promise<void> {
    if (this.closed) return;
    if (this.ctx.state === 'suspended') await this.ctx.resume();
    this.playing = true;
    for (const item of this.pendingBuffers) this.scheduleNode(item.buf, item.ptsUs);
    this.pendingBuffers.length = 0;
  }

  pause(): void {
    this.playing = false;
    void this.ctx.suspend();
  }

  async resume(): Promise<void> {
    if (this.ctx.state === 'suspended') await this.ctx.resume();
    this.playing = true;
  }

  /**
   * SEEK PATTERN: stop all scheduled nodes, drop pending, reset anchor.
   * After swap(), the first new buffer that arrives re-anchors the clock
   * at the new media-PTS — UNLESS setNextAnchor() was called, in which
   * case anchor at the explicit target and skip buffers older than it.
   */
  swap(): void {
    if (this.closed) return;
    for (const n of this.liveNodes) try { n.stop(); } catch { /* noop */ }
    this.liveNodes.length = 0;
    this.pendingBuffers.length = 0;
    this.nextScheduleCtxSec = 0;
    this.anchorCtxSec = -1;
    this.anchorPtsSec = 0;
  }

  /**
   * Audio-swap sync-fix S4: override the next anchor's media-time target.
   * Caller (= main-bridge.setAudioRendition) passes the master-clock
   * value at swap-call time. When the first new buffer arrives, the
   * scheduler anchors at this target (instead of buffer-PTS) and plays
   * the buffer from offset = target - buffer.startPTS. This prevents
   * the master-clock from jumping backwards by the seg-boundary slack
   * (= up to 4 seconds for 4-second segments).
   * Cleared automatically once the next buffer is anchored.
   */
  setNextAnchor(targetMediaSec: number, mode: 'swap' | 'seek' = 'swap'): void {
    if (this.closed) return;
    this.nextAnchorTargetSec = (targetMediaSec > 0) ? targetMediaSec : null;
    this.nextAnchorIssuedAtMs = performance.now();
    // 'swap' = audio-rendition-swap. Master-clock advances via main-bridge's
    //          extrapolation during the catch-up wait (~500-1500ms). Anchor
    //          at target + elapsed so the clock stays monotonic.
    // 'seek' = seek-to-position. Master-clock is PINNED at pendingSeekTimeSec
    //          during warmup (= no advance). Anchor at target EXACTLY when
    //          audio arrives. Forward-project would drop new buffers if
    //          warmup exceeded the buffer-duration (= ~5+ s seek-warmup is
    //          common for keyframe-far seeks).
    this.nextAnchorMode = mode;
  }

  close(): void {
    this.closed = true;
    for (const n of this.liveNodes) try { n.stop(); } catch { /* noop */ }
    this.liveNodes.length = 0;
    if (!this.opts.audioCtx) {
      try { void this.ctx.close(); } catch { /* noop */ }
    }
  }

  /**
   * Master media-clock in seconds. Returns 0 until the first audio
   * buffer is scheduled. After anchoring, advances at real-time relative
   * to the first buffer's media-PTS.
   */
  get currentTimeSec(): number {
    if (this.anchorCtxSec < 0) return 0;
    return this.ctx.currentTime - this.anchorCtxSec + this.anchorPtsSec;
  }

  /** Output latency (codec + speaker) in seconds. Diagnostic only. */
  get outputLatencySec(): number {
    return (this.ctx as AudioContext & { outputLatency?: number }).outputLatency ?? 0;
  }

  /** Underlying AudioContext — for callers that want to share it. */
  get audioContext(): AudioContext {
    return this.ctx;
  }

  // ── Internal ───────────────────────────────────────────────────────

  private scheduleNode(buf: AudioBuffer, ptsUs: number): void {
    // ── Audio-swap sync-fix S4: first-buffer-after-swap anchor logic ──
    //
    // If setNextAnchor(T) was called, we want master-clock to land at T
    // when the first buffer plays. Two cases:
    //   (a) Buffer covers T (= startPts <= T < endPts): play with
    //       offsetSec = T - startPts. Anchor at T. Master-clock continues
    //       smoothly.
    //   (b) Buffer entirely stale (= endPts <= T): drop. Don't anchor.
    //       Wait for next buffer.
    //   (c) Buffer entirely future (= startPts > T): unusual. Anchor at
    //       startPts (= what we'd do without setNextAnchor) and let the
    //       master-clock briefly extrapolate forward via main-bridge's
    //       audioSwapAnchor until this buffer plays out.
    let offsetSec = 0;
    let anchorOverridePtsSec: number | null = null;

    if (this.anchorCtxSec < 0 && this.nextAnchorTargetSec !== null) {
      // For 'swap' (audio-rendition-switch): project target forward by
      // wall-clock elapsed since setNextAnchor was called. Master-clock
      // advances via main-bridge's audioSwapAnchor-extrapolation during
      // the catch-up wait; anchoring at raw target would snap it back.
      // For 'seek': don't project — master-clock is pinned at
      // pendingSeekTimeSec during warmup and the seek-target may be
      // 15+ seconds away from when audio actually arrives.
      const elapsedSec = (this.nextAnchorMode === 'swap')
        ? Math.max(0, (performance.now() - this.nextAnchorIssuedAtMs) / 1000)
        : 0;
      const target = this.nextAnchorTargetSec + elapsedSec;
      const bufStart = ptsUs / 1_000_000;
      const bufEnd   = bufStart + buf.duration;
      if (target >= bufEnd) {
        // (b) Stale. Drop buffer entirely (= no node created).
        return;
      }
      if (target >= bufStart) {
        // (a) Target falls within this buffer. Play from offset.
        offsetSec = target - bufStart;
        anchorOverridePtsSec = target;
      }
      // (c) buffer-start > target: fall through, normal anchor at bufStart.
    }

    let node: AudioBufferSourceNode;
    try {
      node = this.ctx.createBufferSource();
      node.buffer = buf;
      node.connect(this.ctx.destination);
    } catch (e) {
      this.opts.onError?.(e instanceof Error ? e : new Error(String(e)));
      return;
    }

    const now = this.ctx.currentTime;
    const startAt = (this.nextScheduleCtxSec === 0)
      ? now + 0.05                                     // 50 ms head-room for first scheduling
      : Math.max(this.nextScheduleCtxSec, now);
    try {
      // node.start(when, offset) — offsetSec=0 is normal full-buffer playback;
      // > 0 is the S4 sync-fix path.
      if (offsetSec > 0) node.start(startAt, offsetSec);
      else               node.start(startAt);
    } catch (e) {
      this.opts.onError?.(e instanceof Error ? e : new Error(String(e)));
      return;
    }
    this.nextScheduleCtxSec = startAt + (buf.duration - offsetSec);

    // Anchor on first scheduled buffer.
    if (this.anchorCtxSec < 0) {
      this.anchorPtsSec = (anchorOverridePtsSec !== null)
        ? anchorOverridePtsSec
        : ptsUs / 1_000_000;
      this.anchorCtxSec = startAt;
      // Consume the next-anchor-target — only applies to first buffer
      // after swap.
      this.nextAnchorTargetSec = null;
    }

    this.liveNodes.push(node);
    node.onended = () => {
      const idx = this.liveNodes.indexOf(node);
      if (idx >= 0) this.liveNodes.splice(idx, 1);
    };
  }
}
